import { useState } from 'react'
import {
  SESSION_RECORDING_FIRST_NAME_MAX,
  type SessionRecordingPersonPresent,
  type SessionRecordingWithdrawnBy,
} from '@shared/sessions/session-recording'
import {
  ADDABLE_PERSON_ROLES,
  PERSON_ROLE_LABELS,
  WITHDRAWN_BY_OPTIONS,
} from '../utils/recording-labels'
import {
  RecordingDialog,
  dialogDangerButton,
  dialogPrimaryButton,
  dialogSecondaryButton,
} from './recording-dialog'

/**
 * Someone walked in: capture is already paused. Resuming needs the provider
 * to confirm the new person was told and agrees (never pre-ticked).
 */
export function SomeoneJoinedDialog({
  busy,
  error,
  onResume,
  onKeepPaused,
  onTheyDisagree,
}: {
  busy: boolean
  error: string | null
  onResume: (person: SessionRecordingPersonPresent) => void
  onKeepPaused: () => void
  onTheyDisagree: () => void
}) {
  const [role, setRole] = useState<SessionRecordingPersonPresent['role'] | ''>('')
  const [firstName, setFirstName] = useState('')
  const [agreed, setAgreed] = useState(false)
  const canResume = role !== '' && agreed && !busy

  return (
    <RecordingDialog
      title="Someone joined"
      description="Recording is paused. Tell the new person about the recording and ask if they agree before you resume."
      onClose={onKeepPaused}
      busy={busy}
      footer={
        <>
          <button type="button" className={dialogSecondaryButton} onClick={onKeepPaused} disabled={busy}>
            Keep paused
          </button>
          <button type="button" className={dialogSecondaryButton} onClick={onTheyDisagree} disabled={busy}>
            They don’t agree
          </button>
          <button
            type="button"
            className={dialogPrimaryButton}
            disabled={!canResume}
            onClick={() => {
              if (role === '') return
              const name = firstName.trim().slice(0, SESSION_RECORDING_FIRST_NAME_MAX)
              onResume(name ? { role, firstName: name } : { role })
            }}
          >
            {busy ? 'Resuming…' : 'Resume recording'}
          </button>
        </>
      }
    >
      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="text-sm font-semibold text-slate-900">Who joined?</span>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value as SessionRecordingPersonPresent['role'] | '')}
            disabled={busy}
            className="a11y-touch-target mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          >
            <option value="">Choose a role</option>
            {ADDABLE_PERSON_ROLES.map((value) => (
              <option key={value} value={value}>
                {PERSON_ROLE_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-slate-900">
            First name <span className="font-normal text-slate-500">(optional)</span>
          </span>
          <input
            type="text"
            value={firstName}
            maxLength={SESSION_RECORDING_FIRST_NAME_MAX}
            onChange={(event) => setFirstName(event.target.value)}
            disabled={busy}
            autoComplete="off"
            className="a11y-touch-target mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </label>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 px-3 py-3">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            disabled={busy}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-brand-600"
          />
          <span className="text-sm font-medium text-slate-800">The new person has been told and agrees</span>
        </label>
        {error && (
          <p className="text-sm font-semibold text-red-700" role="alert">
            {error}
          </p>
        )}
      </div>
    </RecordingDialog>
  )
}

/** Consent withdrawn: who said stop → stop and delete everything. */
export function WithdrawConsentDialog({
  busy,
  initialBy = null,
  onConfirm,
  onCancel,
  title = 'Stop — consent withdrawn',
  description = 'The recording stops now. The audio, any consent clip and anything drafted from it are deleted from this phone and from Ascend. Only a note that consent was withdrawn is kept.',
  confirmLabel = 'Stop and delete',
  busyLabel = 'Stopping…',
}: {
  busy: boolean
  initialBy?: SessionRecordingWithdrawnBy | null
  onConfirm: (by: SessionRecordingWithdrawnBy) => void
  onCancel: () => void
  title?: string
  description?: string
  confirmLabel?: string
  busyLabel?: string
}) {
  const [by, setBy] = useState<SessionRecordingWithdrawnBy | null>(initialBy)
  return (
    <RecordingDialog
      title={title}
      description={description}
      onClose={onCancel}
      busy={busy}
      alert
      footer={
        <>
          <button type="button" className={dialogSecondaryButton} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={dialogDangerButton}
            disabled={!by || busy}
            onClick={() => by && onConfirm(by)}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </>
      }
    >
      <fieldset className="mt-4" disabled={busy}>
        <legend className="text-sm font-semibold text-slate-900">Who asked to stop?</legend>
        <div className="mt-2 space-y-2">
          {WITHDRAWN_BY_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50"
            >
              <input
                type="radio"
                name="withdrawn-by"
                value={option.value}
                checked={by === option.value}
                onChange={() => setBy(option.value)}
                className="h-4 w-4 border-slate-300 text-red-600"
              />
              <span className="text-sm font-medium text-slate-800">{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </RecordingDialog>
  )
}

export function DiscardRecordingDialog({
  busy,
  onConfirm,
  onCancel,
  description = 'The recording is deleted from this phone and nothing is uploaded. This can’t be undone.',
}: {
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
  description?: string
}) {
  return (
    <RecordingDialog
      title="Discard this recording?"
      description={description}
      onClose={onCancel}
      busy={busy}
      alert
      footer={
        <>
          <button type="button" className={dialogSecondaryButton} onClick={onCancel} disabled={busy}>
            Keep it
          </button>
          <button type="button" className={dialogDangerButton} onClick={onConfirm} disabled={busy}>
            {busy ? 'Discarding…' : 'Discard recording'}
          </button>
        </>
      }
    />
  )
}
